import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { CheckCircle, Zap, Crown, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${""}/api`;

const PLAN_ICONS = { starter: Zap, professional: CheckCircle, advanced: Crown };
const PLAN_COLORS = {
  starter: { border: 'border-blue-200', badge: 'bg-blue-100 text-blue-700', btn: 'bg-blue-600 hover:bg-blue-700', highlight: 'text-blue-600' },
  professional: { border: 'border-teal-300 ring-2 ring-teal-200', badge: 'bg-teal-100 text-teal-700', btn: 'bg-teal-600 hover:bg-teal-700', highlight: 'text-teal-600' },
  advanced: { border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700', btn: 'bg-amber-600 hover:bg-amber-700', highlight: 'text-amber-600' },
};

const BillingPage = ({ currentUser, onUserUpdate }) => {
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [transactions, setTransactions] = useState([]);

  const getToken = () => {
    try { return JSON.parse(localStorage.getItem('pp_auth') || '{}').token; } catch { return ''; }
  };
  const headers = { Authorization: `Bearer ${getToken()}` };

  useEffect(() => {
    axios.get(`${API}/billing/plans`).then(r => setPlans(r.data.plans || {})).catch(() => {});
    if (getToken()) {
      axios.get(`${API}/billing/history`, { headers }).then(r => setTransactions(r.data.transactions || [])).catch(() => {});
    }
  }, []);

  // Poll for payment status on return from Stripe
  const pollStatus = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 5) { setCheckingStatus(false); toast.error('Payment status check timed out.'); return; }
    try {
      const res = await axios.get(`${API}/billing/status/${sessionId}`, { headers });
      if (res.data.payment_status === 'paid') {
        setCheckingStatus(false);
        toast.success(`Upgraded to ${res.data.plan_name}!`);
        // Refresh user
        const me = await axios.get(`${API}/auth/me`, { headers });
        if (me.data.success) onUserUpdate?.(me.data.user);
        // Clean URL
        window.history.replaceState({}, '', window.location.pathname);
        return;
      }
      if (res.data.status === 'expired') { setCheckingStatus(false); toast.error('Payment session expired.'); return; }
      setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
    } catch { setCheckingStatus(false); }
  }, [headers, onUserUpdate]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const cancelled = params.get('cancelled');
    if (sessionId) { setCheckingStatus(true); pollStatus(sessionId); }
    if (cancelled) { toast.info('Payment cancelled.'); window.history.replaceState({}, '', window.location.pathname); }
  }, [pollStatus]);

  const handleSubscribe = async (planId) => {
    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const res = await axios.post(`${API}/billing/checkout`, { plan_id: planId, origin_url: originUrl }, { headers });
      if (res.data.success && res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Checkout failed');
    } finally { setLoading(false); }
  };

  const currentTier = currentUser?.subscription_tier || 'pre_release';
  const isContributor = currentUser?.role === 'contributing' || currentUser?.application_status === 'approved';

  if (checkingStatus) {
    return (
      <div className="max-w-3xl mx-auto p-10 text-center" data-testid="billing-processing">
        <Loader2 className="h-10 w-10 animate-spin text-teal-600 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-slate-800 mb-2">Processing Payment...</h2>
        <p className="text-slate-500">Verifying your subscription upgrade.</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-8" data-testid="billing-page">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-slate-800 mb-2">Choose Your Plan</h1>
        <p className="text-slate-500">Select a subscription plan. Pre-release users get 50% off for life.</p>
        {isContributor && (
          <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg inline-block">
            <Badge className="bg-emerald-100 text-emerald-700 text-sm py-1 px-3">
              Contributing Practitioner — Lifetime Free Access
            </Badge>
            <p className="text-sm text-emerald-600 mt-1">Thank you for your contributions. All features are unlocked.</p>
          </div>
        )}
        {!isContributor && currentTier === 'pre_release' && (
          <Badge className="bg-teal-100 text-teal-700 mt-3 text-sm py-1 px-3">
            Pre-Release Access — 50% Off When Billing Activates
          </Badge>
        )}
        {!isContributor && currentTier !== 'pre_release' && (
          <Badge className="bg-teal-100 text-teal-700 mt-3 text-sm py-1 px-3">
            Current Plan: {currentTier.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
          </Badge>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {Object.entries(plans).map(([id, plan]) => {
          const colors = PLAN_COLORS[id] || PLAN_COLORS.starter;
          const Icon = PLAN_ICONS[id] || Zap;
          const isCurrentPlan = currentTier === id;
          const isMostPopular = id === 'professional';

          return (
            <Card key={id} className={`relative ${colors.border} ${isMostPopular ? 'shadow-lg' : 'shadow-sm'} transition-all hover:shadow-md`}
              data-testid={`plan-card-${id}`}>
              {isMostPopular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-teal-600 text-white text-xs">Most Popular</Badge>
                </div>
              )}
              <CardContent className="p-6 space-y-5">
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colors.badge}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-800">{plan.name}</h3>
                </div>

                <div>
                  <span className={`text-4xl font-bold ${colors.highlight}`}>${plan.price}</span>
                  <span className="text-slate-500 text-sm">/month</span>
                </div>

                <p className="text-sm text-slate-500">{plan.description}</p>

                <ul className="space-y-2">
                  {plan.features?.map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                      <CheckCircle className={`h-4 w-4 flex-shrink-0 ${colors.highlight}`} />
                      {f}
                    </li>
                  ))}
                </ul>

                <Button
                  className={`w-full text-white ${isCurrentPlan || isContributor ? 'bg-slate-400 cursor-not-allowed' : colors.btn}`}
                  disabled={loading || isCurrentPlan || isContributor}
                  onClick={() => handleSubscribe(id)}
                  data-testid={`subscribe-${id}-btn`}
                >
                  {isContributor ? 'Free Access' : isCurrentPlan ? 'Current Plan' : loading ? 'Loading...' : `Subscribe to ${plan.name}`}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Billing History */}
      {transactions.length > 0 && (
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Billing History</h2>
            <div className="space-y-3">
              {transactions.map((t, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0 text-sm">
                  <div>
                    <span className="font-medium text-slate-800">{t.plan_name}</span>
                    <span className="text-slate-400 ml-2">{new Date(t.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-slate-600">${t.amount}</span>
                    <Badge className={t.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                      {t.payment_status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BillingPage;
